// The frontend data layer (2026-09-30 slice 1): one QueryClient shared by
// the whole app, backed by an IndexedDB-persisted cache so the owner's rule
// holds literally — "I don't ever want to see blank data points if there is
// data." On every visit (reload, fresh login, wherever), whatever was last
// successfully fetched renders on the FIRST frame, then a background
// refetch updates it. An empty state is only ever shown once the server has
// actually answered "there is nothing" — see AppData.tsx and the three
// NO_CONNECTIONS_EMPTY_STATE sites this slice touches for the other half of
// that contract.
//
// Storage: idb-keyval over IndexedDB via @tanstack/query-async-storage-
// persister's createAsyncStoragePersister — idb-keyval's get/set/del already
// match the {getItem,setItem,removeItem} shape that persister wants, so no
// adapter shim is needed. IndexedDB (not localStorage) because the
// persisted payload includes GET /api/nodes at ~1.3MB — comfortably past
// what's reasonable to hold synchronously in localStorage.
//
// Persisted set: ONLY queries explicitly tagged `meta: { persist: true }`
// (see useGlobalQuery.ts, the one call site every persisted query goes
// through) are written to IndexedDB — enforced by `shouldDehydrateQuery`
// below, not by convention. Auth/session responses never go through
// useGlobalQuery and are therefore never persisted. To persist a new query
// in a later slice, route it through useGlobalQuery (or otherwise set
// `meta: { persist: true }` on it) — nothing else needs to change here.
import { QueryClient, type Query } from "@tanstack/react-query";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { get, set, del } from "idb-keyval";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A failed background refetch must never blank out already-shown
      // data (BUILD THIS #9 / the owner's rule) — react-query already
      // keeps `data` on a refetch error by default; these two just make
      // sure a slow/offline backend doesn't retry-storm or treat a stale
      // render as unacceptable.
      retry: 1,
      staleTime: 0,
    },
  },
});

// idb-keyval's default store (one object store, `keyval-store` DB) is fine
// here — this is the only thing in the app using IndexedDB, no need for a
// dedicated custom store.
export const persister = createAsyncStoragePersister({
  storage: {
    getItem: (key: string) => get(key),
    setItem: (key: string, value: string) => set(key, value),
    removeItem: (key: string) => del(key),
  },
  key: "amalgamesh:query-cache",
});

// A build's own commit sha (vite.config.ts's `define`, falling back to a
// build-time timestamp outside a git checkout) — passed as
// persistOptions.buster in main.tsx. react-query-persist-client compares
// this against the buster stored alongside the persisted cache and discards
// the whole cache on a mismatch, so a schema/shape change that ships with a
// new build can never get hydrated into code that no longer expects it.
export const BUILD_VERSION: string = typeof __BUILD_VERSION__ === "string" ? __BUILD_VERSION__ : "dev";

// ~7 days: long enough that a weekly-ish visitor still gets instant data on
// return, short enough that genuinely abandoned state doesn't linger
// forever. Applies per dehydrated query, not to the persisted blob overall
// — see persistQueryClient's own docs.
export const PERSIST_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

// Only ever persist a query that opted in via `meta.persist === true`
// (useGlobalQuery.ts), and only once it has real data (`success`) — a
// pending/erroring query has nothing worth writing to disk, and writing an
// error state would risk hydrating a future session straight into a stale
// failure.
export function shouldDehydrateQuery(query: Query): boolean {
  return query.state.status === "success" && query.meta?.persist === true;
}

// Logout (AuthContext.tsx) and an auth-identity change both need to wipe
// EVERY trace of the previous session's data, not just the in-memory
// QueryClient — otherwise the next login (same browser, different account)
// would flash the previous user's last-known nodes/packets/stats on first
// paint before the fresh fetch lands, which is exactly the kind of "wrong
// data shown as if it were current" the persistence layer must never do.
// `persister.removeClient()` deletes the IndexedDB entry outright (not just
// an in-memory clear), so a reload right after logout also starts clean.
export async function clearPersistedQueryCache(): Promise<void> {
  queryClient.clear();
  await persister.removeClient();
}
